import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import BAModeNav from '@/components/BAModeNav';
import { toast, Toaster } from 'sonner';
import { LayoutGrid, Upload, Download, Lock, Save, Trash2, Plus } from 'lucide-react';
import { fileToBitmap, urlToBitmap, drawCover, drawLogoWatermark, knockoutWhite } from '@/lib/imageUtils';

const PRESETS = [
  { key: 'square', label: 'Square 1:1', hint: 'Feed post', w: 1080, h: 1080 },
  { key: 'portrait', label: 'Portrait 4:5', hint: 'Feed post', w: 1080, h: 1350 },
  { key: 'story', label: 'Story 9:16', hint: 'Stories / Reels', w: 1080, h: 1920 },
  { key: 'landscape', label: 'Landscape 16:9', hint: 'Facebook', w: 1080, h: 608 },
];
const BRAND_SWATCHES = ['#FF5A1F', '#0f172a', '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#0891b2'];
const BG_SWATCHES = ['#ffffff', '#0f172a', '#f4f4ef', '#FF5A1F', '#111827'];

const grid = (cols, rows) => {
  const c = [];
  for (let r = 0; r < rows; r++) for (let x = 0; x < cols; x++) c.push({ x: x / cols, y: r / rows, w: 1 / cols, h: 1 / rows });
  return c;
};
const LAYOUTS = [
  { key: 'g2h', cat: 'Grids', label: '2 · Side by side', cells: grid(2, 1) },
  { key: 'g2v', cat: 'Grids', label: '2 · Stacked', cells: grid(1, 2) },
  { key: 'g3r', cat: 'Grids', label: '3 · Row', cells: grid(3, 1) },
  { key: 'g3c', cat: 'Grids', label: '3 · Column', cells: grid(1, 3) },
  { key: 'g4', cat: 'Grids', label: '4 · Grid', cells: grid(2, 2) },
  { key: 'g6', cat: 'Grids', label: '6 · Grid', cells: grid(3, 2) },
  { key: 'g9', cat: 'Grids', label: '9 · Grid', cells: grid(3, 3) },
  { key: 'f3', cat: 'Feature', label: '3 · Feature', cells: [{ x: 0, y: 0, w: 2 / 3, h: 1 }, { x: 2 / 3, y: 0, w: 1 / 3, h: 1 / 2 }, { x: 2 / 3, y: 1 / 2, w: 1 / 3, h: 1 / 2 }] },
  { key: 'f5', cat: 'Feature', label: '5 · Feature', cells: [{ x: 0, y: 0, w: 0.6, h: 1 }, { x: 0.6, y: 0, w: 0.2, h: 0.5 }, { x: 0.8, y: 0, w: 0.2, h: 0.5 }, { x: 0.6, y: 0.5, w: 0.2, h: 0.5 }, { x: 0.8, y: 0.5, w: 0.2, h: 0.5 }] },
  { key: 'f7', cat: 'Feature', label: '7 · Feature', cells: [{ x: 0, y: 0, w: 0.5, h: 1 }, { x: 0.5, y: 0, w: 0.25, h: 1 / 3 }, { x: 0.75, y: 0, w: 0.25, h: 1 / 3 }, { x: 0.5, y: 1 / 3, w: 0.25, h: 1 / 3 }, { x: 0.75, y: 1 / 3, w: 0.25, h: 1 / 3 }, { x: 0.5, y: 2 / 3, w: 0.25, h: 1 / 3 }, { x: 0.75, y: 2 / 3, w: 0.25, h: 1 / 3 }] },
  { key: 's4', cat: 'Filmstrip', label: '4 · Filmstrip', cells: grid(4, 1) },
  { key: 's4v', cat: 'Filmstrip', label: '4 · Strip (tall)', cells: grid(1, 4) },
];
const CATS = ['Grids', 'Feature', 'Filmstrip'];

const TEMPLATES = [
  { key: 'pair', label: 'Before / After Pair', layout: 'g2h', preset: 'square', gap: 8, bg: '#0f172a' },
  { key: 'story3', label: '3-Step Job Story', layout: 'g3c', preset: 'story', gap: 10, bg: '#0f172a' },
  { key: 'grid4', label: '4-Up Showcase', layout: 'g4', preset: 'square', gap: 12, bg: '#ffffff' },
  { key: 'hero5', label: 'Feature Hero', layout: 'f5', preset: 'portrait', gap: 10, bg: '#111827' },
  { key: 'grid9', label: '9-Grid Portfolio', layout: 'g9', preset: 'square', gap: 6, bg: '#ffffff' },
  { key: 'strip', label: 'Filmstrip Reel', layout: 's4', preset: 'landscape', gap: 8, bg: '#0f172a' },
];

function Toggle({ on, set, tid, children }) {
  return (
    <button onClick={() => set(!on)} data-testid={tid}
      className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-display font-bold uppercase tracking-wide ${on ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${on ? 'bg-brand' : 'bg-slate-300'}`} /> {children}
    </button>
  );
}

export default function CollagePage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const canvasRef = useRef(null);
  const fileMultiRef = useRef(null);
  const slotInputRef = useRef(null);
  const slotIdxRef = useRef(0);

  const [presetKey, setPresetKey] = useState('square');
  const [layoutKey, setLayoutKey] = useState('g4');
  const [gap, setGap] = useState(10);
  const [bg, setBg] = useState('#ffffff');
  const [brand, setBrand] = useState('#FF5A1F');
  useEffect(() => { if (user?.brand_color) setBrand(user.brand_color); }, [user?.brand_color]);
  const [photos, setPhotos] = useState([]); // ImageBitmaps
  const [savingGallery, setSavingGallery] = useState(false);

  const [showFooter, setShowFooter] = useState(false);
  const [footerText, setFooterText] = useState(user?.business_name || '');
  const [showLogo, setShowLogo] = useState(true);
  const [logoPos, setLogoPos] = useState('br');
  const [logoSize, setLogoSize] = useState('m');
  const [logoBmp, setLogoBmp] = useState(null);
  const [logoOriginal, setLogoOriginal] = useState(null);
  const [logoTransparent, setLogoTransparent] = useState(false);
  const [logoThreshold, setLogoThreshold] = useState(238);

  useEffect(() => {
    api.get('/me/logo').then((r) => {
      const raw = r.data?.original || r.data?.data_url;
      if (!raw) return;
      setLogoOriginal(raw); setLogoTransparent(!!r.data?.transparent); setLogoThreshold(r.data?.threshold || 238);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!logoOriginal) { setLogoBmp(null); return; }
    const build = async () => {
      try {
        const src = logoTransparent ? await knockoutWhite(logoOriginal, logoThreshold) : logoOriginal;
        const bmp = await urlToBitmap(src);
        if (!cancelled) setLogoBmp(bmp);
      } catch { /* ignore */ }
    };
    const t = setTimeout(build, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [logoOriginal, logoTransparent, logoThreshold]);

  const preset = PRESETS.find((p) => p.key === presetKey);
  const layout = LAYOUTS.find((l) => l.key === layoutKey);
  const n = layout.cells.length;

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const { w, h } = preset;
    const footerH = showFooter ? Math.round(112 * (w / 1080)) : 0;
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    const areaH = h - footerH;
    const scale = w / 1080;
    const gpx = Math.round(gap * scale);

    ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);

    layout.cells.forEach((c, i) => {
      const rx = Math.round(c.x * w + gpx / 2);
      const ry = Math.round(c.y * areaH + gpx / 2);
      const rw = Math.round(c.w * w - gpx);
      const rh = Math.round(c.h * areaH - gpx);
      if (rw <= 0 || rh <= 0) return;
      if (photos[i]) {
        drawCover(ctx, photos[i], rx, ry, rw, rh);
      } else {
        ctx.fillStyle = '#1e293b'; ctx.fillRect(rx, ry, rw, rh);
        ctx.fillStyle = '#64748b';
        ctx.font = `700 ${Math.round(28 * scale)}px Arial`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(`Photo ${i + 1}`, rx + rw / 2, ry + rh / 2);
      }
    });

    if (showFooter) {
      ctx.fillStyle = brand; ctx.fillRect(0, h - footerH, w, footerH);
      ctx.fillStyle = '#ffffff';
      ctx.font = `900 ${Math.round(40 * scale)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText((footerText || 'Your business name').toUpperCase(), w / 2, h - footerH / 2);
    }

    if (showLogo && logoBmp) {
      drawLogoWatermark(ctx, logoBmp, { areaW: w, areaH, pos: logoPos, size: logoSize, scale, transparent: logoTransparent });
    }
  }, [photos, layoutKey, presetKey, gap, bg, brand, showFooter, footerText, showLogo, logoPos, logoSize, logoBmp, logoTransparent, layout, preset]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = Math.max(0, 9 - photos.length);
    const take = files.slice(0, room);
    if (files.length > room) toast.message(`Added ${room} — a collage holds up to 9 photos.`);
    try {
      const bmps = await Promise.all(take.map(fileToBitmap));
      setPhotos((p) => [...p, ...bmps].slice(0, 9));
    } catch { toast.error('Could not read one of those images'); }
  };

  const onSlotPick = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const bmp = await fileToBitmap(file);
        setPhotos((p) => { const c = [...p]; c[slotIdxRef.current] = bmp; return c; });
      } catch { toast.error('Could not read that image'); }
    }
    e.target.value = '';
  };
  const openSlot = (i) => { slotIdxRef.current = i; slotInputRef.current?.click(); };
  const clearSlot = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));
  const clearAll = () => setPhotos([]);

  const applyTemplate = (t) => {
    setLayoutKey(t.layout); setPresetKey(t.preset); setGap(t.gap); setBg(t.bg);
    toast.success(`Template applied: ${t.label}`);
  };

  const filled = photos.length;

  const download = () => {
    if (filled === 0) { toast.error('Add at least one photo first'); return; }
    const cv = canvasRef.current;
    const trigger = (url, revoke) => {
      const a = document.createElement('a');
      a.href = url; a.download = `collage-${layoutKey}-${presetKey}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      if (revoke) URL.revokeObjectURL(url);
      toast.success('Collage downloaded — ready to post!');
    };
    try { cv.toBlob((b) => { if (b) trigger(URL.createObjectURL(b), true); else trigger(cv.toDataURL('image/png'), false); }, 'image/png'); }
    catch { trigger(cv.toDataURL('image/png'), false); }
  };

  const saveToGallery = async () => {
    if (filled === 0) { toast.error('Add at least one photo first'); return; }
    setSavingGallery(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob((b) => res(b), 'image/png'));
      if (!blob) throw new Error('render');
      const fd = new FormData();
      fd.append('file', blob, `collage-${Date.now()}.png`);
      fd.append('caption', '');
      fd.append('kind', 'collage');
      await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Saved to your gallery');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to gallery');
    } finally { setSavingGallery(false); }
  };

  if (user && !isPro) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="collage-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Build photo collages of your work for social media. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="collage-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title="Photo Collage Maker" noindex />
      <Toaster richColors position="top-right" />
      <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Marketing</p>
      <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1 mb-4 flex items-center gap-3"><LayoutGrid className="text-brand" size={36} /> Collage</h1>
      <BAModeNav />

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Canvas */}
        <div className="tm-card p-3 sm:p-4">
          <canvas ref={canvasRef} data-testid="collage-canvas" className="w-full h-auto border border-slate-200 bg-white" style={{ maxHeight: '70vh', objectFit: 'contain' }} />
          {/* Slots */}
          <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="collage-slots">
            {Array.from({ length: n }).map((_, i) => (
              <div key={i} className="relative">
                <button onClick={() => openSlot(i)} data-testid={`collage-slot-${i}`}
                  className="w-14 h-14 border border-slate-300 hover:border-brand flex items-center justify-center overflow-hidden bg-slate-50">
                  {photos[i] ? <span className="font-mono text-[10px] text-emerald-700">#{i + 1}✓</span> : <Plus size={16} className="text-slate-400" />}
                </button>
                {photos[i] && (
                  <button onClick={() => clearSlot(i)} data-testid={`collage-slot-clear-${i}`} className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">×</button>
                )}
              </div>
            ))}
            <input ref={slotInputRef} type="file" accept="image/*" className="hidden" onChange={onSlotPick} />
          </div>
        </div>

        {/* Controls */}
        <div className="tm-card p-4 space-y-4">
          <input ref={fileMultiRef} type="file" accept="image/*" multiple className="hidden" data-testid="collage-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <div className="flex gap-2">
            <button onClick={() => fileMultiRef.current?.click()} data-testid="collage-add-photos" className="tm-btn flex-1"><Upload size={16} /> Add photos ({filled}/9)</button>
            {filled > 0 && <button onClick={clearAll} data-testid="collage-clear" className="tm-btn tm-btn-ghost"><Trash2 size={16} /></button>}
          </div>

          <div data-testid="collage-templates">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Templates · one tap</p>
            <div className="grid grid-cols-2 gap-1">
              {TEMPLATES.map((t) => (
                <button key={t.key} onClick={() => applyTemplate(t)} data-testid={`collage-template-${t.key}`}
                  className="border border-slate-300 hover:border-brand hover:text-brand py-1.5 px-2 text-[10px] uppercase text-left text-slate-700 font-semibold">{t.label}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Layout</p>
            {CATS.map((cat) => (
              <div key={cat} className="mb-2">
                <p className="text-[10px] uppercase text-slate-400 mb-1 font-semibold">{cat}</p>
                <div className="grid grid-cols-2 gap-1">
                  {LAYOUTS.filter((l) => l.cat === cat).map((l) => (
                    <button key={l.key} onClick={() => setLayoutKey(l.key)} data-testid={`collage-layout-${l.key}`}
                      className={`border py-1.5 text-[10px] font-mono uppercase ${layoutKey === l.key ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600 hover:border-brand'}`}>{l.label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Format</p>
            <div className="grid grid-cols-2 gap-1">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => setPresetKey(p.key)} data-testid={`collage-preset-${p.key}`}
                  className={`border px-2 py-1.5 text-left ${presetKey === p.key ? 'border-slate-200 bg-slate-50' : 'border-slate-300'}`}>
                  <span className="block text-xs font-bold">{p.label}</span>
                  <span className="block text-[10px] text-slate-500">{p.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Spacing / border</p>
              <span className="font-mono text-[11px] text-slate-600" data-testid="collage-gap-value">{gap}px</span>
            </div>
            <input type="range" min={0} max={48} step={1} value={gap} onChange={(e) => setGap(parseInt(e.target.value))} data-testid="collage-gap" className="w-full accent-brand" />
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Background</p>
            <div className="flex gap-2 flex-wrap">
              {BG_SWATCHES.map((c) => (
                <button key={c} onClick={() => setBg(c)} data-testid={`collage-bg-${c.replace('#', '')}`} aria-label={c}
                  className={`w-8 h-8 border ${bg.toLowerCase() === c.toLowerCase() ? 'border-slate-200 scale-110' : 'border-slate-300'}`} style={{ background: c }} />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Brand colour</p>
            <div className="flex gap-2 flex-wrap">
              {BRAND_SWATCHES.map((c) => (
                <button key={c} onClick={() => setBrand(c)} data-testid={`collage-brand-${c.replace('#', '')}`} aria-label={c}
                  className={`w-8 h-8 border ${brand.toLowerCase() === c.toLowerCase() ? 'border-slate-200 scale-110' : 'border-slate-300'}`} style={{ background: c }} />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Toggle on={showFooter} set={setShowFooter} tid="collage-toggle-footer">Business name</Toggle>
            <Toggle on={showLogo} set={setShowLogo} tid="collage-toggle-logo">Logo</Toggle>
          </div>
          {showFooter && (
            <input value={footerText} onChange={(e) => setFooterText(e.target.value)} data-testid="collage-footer-text" placeholder="Your business name" className="tm-input" />
          )}
          {showLogo && logoBmp && (
            <div className="space-y-2">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Logo position</p>
                <div className="grid grid-cols-4 gap-1">
                  {[['tl', 'TL'], ['tr', 'TR'], ['bl', 'BL'], ['br', 'BR']].map(([v, l]) => (
                    <button key={v} onClick={() => setLogoPos(v)} data-testid={`collage-logopos-${v}`}
                      className={`border py-1.5 text-[10px] font-mono ${logoPos === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {[['s', 'Small'], ['m', 'Medium'], ['l', 'Large']].map(([v, l]) => (
                  <button key={v} onClick={() => setLogoSize(v)} data-testid={`collage-logosize-${v}`}
                    className={`border py-1.5 text-[10px] font-mono uppercase ${logoSize === v ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-300 text-slate-600'}`}>{l}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none border border-slate-200 px-3 py-2">
                <input data-testid="collage-logo-transparent" type="checkbox" checked={logoTransparent} onChange={(e) => setLogoTransparent(e.target.checked)} className="w-4 h-4 accent-brand" />
                <span className="text-[11px] uppercase tracking-wider text-slate-700 font-semibold">Transparent logo</span>
              </label>
            </div>
          )}
          {showLogo && !logoBmp && (
            <p className="text-[11px] text-slate-400">No logo found — add one under <a href="/settings?tab=branding" className="text-brand underline">Settings → Branding</a>.</p>
          )}

          <div className="pt-2 border-t border-slate-100 space-y-2">
            <button onClick={download} data-testid="collage-download" className="tm-btn w-full"><Download size={16} /> Download image</button>
            <button onClick={saveToGallery} disabled={savingGallery} data-testid="collage-save-gallery" className="tm-btn tm-btn-ghost w-full">
              <Save size={16} /> {savingGallery ? 'Saving…' : 'Save to gallery'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
